import { isInternalSurface } from '@/lib/internal-surfaces'

/** Only this fixed vocabulary leaves the browser/server, never query text. */
export function searchAnalyticsCategory(query: string): string {
  const text = query.normalize('NFKC').toLowerCase()
  for (const [category, pattern] of [
    ['water-filter', /\b(?:water filter|filter replacement|filter cartridge)\b/],
    ['wine-cooler', /\b(?:wine cooler|wine fridge|wine refrigerator)\b/],
    ['ice-maker', /\b(?:ice maker|icemaker|nugget ice)\b/],
    ['kegerator', /\bkegerator\b/],
    ['freezer', /\bfreezer\b/],
    ['refrigerator', /\b(?:fridge|refrigerator)\b/],
  ] as const) {
    if (pattern.test(text)) return category
  }
  return 'other'
}

export interface SearchAnalyticsTraffic {
  hostname: string
  pathname: string
  userAgent: string
  automated?: boolean
  internal?: boolean
}

/** Unknown server context fails closed; no claim that UA checks prove humanity. */
export function allowSearchAnalytics(traffic?: SearchAnalyticsTraffic): boolean {
  return Boolean(traffic && traffic.hostname === 'fridge.com' && traffic.userAgent &&
    !traffic.automated && !traffic.internal && !isInternalSurface(traffic.pathname) &&
    !/bot|crawl|spider|headless|prerender|playwright|puppeteer|selenium/i.test(traffic.userAgent))
}

export function safeSearchProperties(query: string, resultsCount: number) {
  return {
    query_category: searchAnalyticsCategory(query),
    results_count: Number.isFinite(resultsCount) ? Math.max(0, Math.floor(resultsCount)) : 0,
    query_storage: 'category_only',
  }
}

const QUERY_KEYS = /^\$?(?:q|query|search|search_query|searchquery|search_term|searchterm|keyword|term)$/i

/** Strip free-text search parameters from pageview, referrer and nested SDK URLs. */
export function stripSearchQueryFromUrl(value: string): string {
  try {
    const relative = value.startsWith('/') && !value.startsWith('//')
    const url = relative ? new URL(value, 'https://fridge.com') : new URL(value)
    if (url.protocol !== 'https:' && url.protocol !== 'http:') return value
    for (const key of [...url.searchParams.keys()]) {
      if (QUERY_KEYS.test(key)) url.searchParams.delete(key)
    }
    if (/^\/search\/?$/.test(url.pathname)) url.hash = ''
    return relative ? `${url.pathname}${url.search}${url.hash}` : url.toString()
  } catch {
    return value
  }
}

/** Also remove legacy query properties, including persisted person-property bags. */
export function sanitizeSearchAnalyticsProperties(properties: Record<string, unknown>, pathname = ''): Record<string, unknown> {
  const clean = (value: unknown): unknown => {
    if (typeof value === 'string') return stripSearchQueryFromUrl(value)
    if (Array.isArray(value)) return value.map(clean)
    if (value && typeof value === 'object') {
      return Object.fromEntries(Object.entries(value)
        .filter(([key]) => !QUERY_KEYS.test(key) && !(/^\/search\/?$/.test(pathname) && /^\$?title$/i.test(key)))
        .map(([key, item]) => [key, clean(item)]))
    }
    return value
  }
  return clean(properties) as Record<string, unknown>
}

export function sanitizeSearchAnalyticsEvent<T extends { event: string; properties?: Record<string, unknown> }>(
  event: T,
  traffic: SearchAnalyticsTraffic,
): T | null {
  const searchPage = /^\/search\/?$/.test(traffic.pathname)
  if ((searchPage || event.event.startsWith('search_')) && !allowSearchAnalytics(traffic)) return null
  // Autocaptured DOM text may repeat the input. Explicit outcomes use categories.
  if (searchPage && event.event === '$autocapture') return null
  return { ...event, properties: sanitizeSearchAnalyticsProperties(event.properties || {}, traffic.pathname) }
}
